/**
 * PushAdequacyReport — /#/reports/push-adequacy
 *
 * Tenant-level config (rep_self_check vs manager_judges), retry counts,
 * and a breakdown of auto-LOSS reasons. Driven by
 * tenant_escalation_config + push_adequacy_queue.
 */
import { useEffect, useMemo, useState } from "react";
import ReportShell, { thStyle, tdStyle, BLACK, RED, SLATE, BORDER, EmptyState, LoadingState } from "./_ReportShell";
import {
  AdequacyConfigRow,
  AdequacyStatsRow,
  downloadCsv,
  listAdequacyConfig,
  listAdequacyStats,
  toCsv,
} from "@/lib/reports-saturday";

export default function PushAdequacyReport() {
  const [config, setConfig] = useState<AdequacyConfigRow[] | null>(null);
  const [stats, setStats] = useState<AdequacyStatsRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, s] = await Promise.all([listAdequacyConfig(), listAdequacyStats()]);
        if (!cancelled) {
          setConfig(c);
          setStats(s);
        }
      } catch (err: any) {
        console.error("[PushAdequacyReport]", err);
        if (!cancelled) setError(err?.message ?? "Failed to load");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const csv = useMemo(() => {
    if (!stats) return "";
    return toCsv([
      {
        tenant_id: stats.tenant_id,
        total_submitted: stats.total_submitted,
        pending: stats.pending,
        adequate: stats.adequate,
        inadequate: stats.inadequate,
        auto_loss: stats.auto_loss,
        retries: stats.retries,
      },
    ]);
  }, [stats]);

  return (
    <ReportShell
      title="PUSH Adequacy"
      subtitle="Tenant adequacy mode, retry stats, and auto-LOSS reasons."
      rightLabel="PUSH Adequacy"
      onExport={stats ? () => downloadCsv("push-adequacy.csv", csv) : undefined}
    >
      {error ? (
        <EmptyState message={`Could not load: ${error}`} />
      ) : !config || !stats ? (
        <LoadingState />
      ) : (
        <>
          {/* Config */}
          <div style={{ marginBottom: 24, border: `1px solid ${BORDER}`, borderRadius: 8, background: "#FFFFFF" }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER}`, fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: SLATE }}>
              Tenant config
            </div>
            {config.length === 0 ? (
              <div style={{ padding: 24, color: SLATE, fontSize: 13 }}>
                No config row for this tenant. Default is <strong>rep_self_check</strong>.
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {config.map((c) => (
                    <tr key={c.tenant_id}>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>Adequacy mode</td>
                      <td style={tdStyle}>
                        <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, background: c.adequacy_check_mode === "manager_judges" ? "#FEF3C7" : "#DCFCE7", color: c.adequacy_check_mode === "manager_judges" ? "#92400E" : "#166534", fontSize: 11, fontWeight: 700 }}>
                          {c.adequacy_check_mode}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>Escalation contact</td>
                      <td style={tdStyle}>{c.escalation_contact_name ?? "—"} <span style={{ color: SLATE, fontSize: 11 }}>({c.escalation_contact_email ?? "—"})</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Stats */}
          <div style={{ marginBottom: 24, border: `1px solid ${BORDER}`, borderRadius: 8, background: "#FFFFFF" }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER}`, fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: SLATE }}>
              Adequacy queue stats
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Total</th>
                  <th style={thStyle}>Pending</th>
                  <th style={thStyle}>Adequate</th>
                  <th style={thStyle}>Inadequate</th>
                  <th style={thStyle}>Auto-LOSS</th>
                  <th style={thStyle}>Retries</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 800 }}>{stats.total_submitted}</td>
                  <td style={tdStyle}>{stats.pending}</td>
                  <td style={{ ...tdStyle, color: "#16A34A" }}>{stats.adequate}</td>
                  <td style={{ ...tdStyle, color: "#CA8A04" }}>{stats.inadequate}</td>
                  <td style={{ ...tdStyle, color: RED, fontWeight: 700 }}>{stats.auto_loss}</td>
                  <td style={tdStyle}>{stats.retries}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Auto-LOSS reasons */}
          <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, background: "#FFFFFF" }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER}`, fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: SLATE }}>
              Auto-LOSS reasons
            </div>
            {stats.auto_loss_reasons.length === 0 ? (
              <div style={{ padding: 24, color: SLATE, fontSize: 13 }}>None — no auto-LOSS conversions on record.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Reason</th>
                    <th style={thStyle}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.auto_loss_reasons.map((r) => (
                    <tr key={r.reason}>
                      <td style={tdStyle}>{r.reason}</td>
                      <td style={tdStyle}>{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </ReportShell>
  );
}
